import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

interface Project {
  id: number;
  name: string;
  description?: string;
  organization_id: number;
  organization_name: string;
  owner_name?: string | null;
  owner_email?: string | null;
  members_count: number;
  requests_count: number;
  tickets_count: number;
  progress: number;
  is_demo?: number;
  is_trial?: number;
  is_account_project?: number;
  is_tech_support_project?: number;
  show_motionode_sales?: number;
  show_motionode_support?: number;
  commercial_status?: string | null;
  initial_saved_minutes?: number;
  update_saved_minutes?: number;
  created_at: string;
  updated_at: string;
}

interface ProjectsResponse {
  data: Project[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ProjectStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  suspendedProjects: number;
  cancelledProjects: number;
  avgProgress: number;
  newToday: number;
  newThisMonth: number;
}

interface ProjectFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  organization_id?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: ProjectsResponse['meta'];
  message?: string;
}

const transformErrorResponse = (error: any) => {
  if (error?.data?.message) {
    return { error: error.data.message };
  }
  return { error: 'An error occurred' };
};

export const projectsApi = createApi({
  reducerPath: 'projectsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/projects',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Project'],
  endpoints: (builder) => ({
    getProjects: builder.query<ProjectsResponse, ProjectFilters>({
      query: (filters = {}) => ({
        url: '/',
        params: filters,
      }),
      transformResponse: (response: ApiResponse<Project[]>) => {
        if (response.success) {
          return {
            data: response.data ?? [],
            meta: response.meta ?? {
              page: 1,
              limit: response.data?.length ?? 0,
              total: response.data?.length ?? 0,
              totalPages: 1,
            },
          };
        }
        throw new Error(response.message || 'Failed to fetch projects');
      },
      transformErrorResponse,
      providesTags: ['Project'],
    }),
    getProjectStats: builder.query<ProjectStats, void>({
      query: () => '/stats',
      transformResponse: (response: ApiResponse<ProjectStats>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch project statistics');
      },
      transformErrorResponse,
      providesTags: ['Project'],
    }),
    getProjectById: builder.query<Project, number>({
      query: (id) => `/${id}`,
      transformResponse: (response: ApiResponse<Project>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch project');
      },
      transformErrorResponse,
      providesTags: ['Project'],
    }),
    getProjectMembers: builder.query<any[], number>({
      query: (id) => `/${id}/members`,
      transformResponse: (response: ApiResponse<any[]>) => {
        if (response.success) {
          return response.data ?? [];
        }
        throw new Error(response.message || 'Failed to fetch project members');
      },
      transformErrorResponse,
      providesTags: ['Project'],
    }),
    updateProject: builder.mutation<Project, { id: number; name?: string; description?: string }>({
      query: ({ id, name, description }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: { name, description },
      }),
      transformResponse: (response: ApiResponse<Project>) => {
        if (!response.success || !response.data) {
          throw new Error(response.message || 'Failed to update project');
        }
        return response.data;
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    deleteProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to delete project');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    setDemoProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/set-demo`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to set demo project');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    unsetDemoProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/unset-demo`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to unset demo project');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    // Trial Project mutations
    setTrialProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/set-trial`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to set trial project');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    unsetTrialProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/unset-trial`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to unset trial project');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    // Account Project mutations
    setAccountProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/set-account-project`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to set account project');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    unsetAccountProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/unset-account-project`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to unset account project');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    // Tech Support Project mutations
    setTechSupportProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/set-tech-support-project`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to set tech support project');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    unsetTechSupportProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/unset-tech-support-project`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to unset tech support project');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    setShowMotionodeSales: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/set-show-motionode-sales`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to enable Motionode Sales');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    unsetShowMotionodeSales: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/unset-show-motionode-sales`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to disable Motionode Sales');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    setShowMotionodeSupport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/set-show-motionode-support`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to enable Motionode Support');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),
    unsetShowMotionodeSupport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/unset-show-motionode-support`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<null>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to disable Motionode Support');
        }
      },
      transformErrorResponse,
      invalidatesTags: ['Project'],
    }),

  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectStatsQuery,
  useGetProjectByIdQuery,
  useGetProjectMembersQuery,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useSetDemoProjectMutation,
  useUnsetDemoProjectMutation,
  useSetTrialProjectMutation,
  useUnsetTrialProjectMutation,
  useSetAccountProjectMutation,
  useUnsetAccountProjectMutation,
  useSetTechSupportProjectMutation,
  useUnsetTechSupportProjectMutation,
  useSetShowMotionodeSalesMutation,
  useUnsetShowMotionodeSalesMutation,
  useSetShowMotionodeSupportMutation,
  useUnsetShowMotionodeSupportMutation,
} = projectsApi; 